import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import InteractiveTable from '../components/InteractiveTable';
import { SortableTableHeader, sortData } from '../components/SortableTableHeader';
import { useAuth } from '../contexts/AuthContext';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import {
  ClipboardCheck, Plus, ChevronLeft, Check, AlertTriangle, ArrowUpDown, Save,
  RotateCcw, CheckCircle2, XCircle, Minus, PlusCircle
} from 'lucide-react';

const STATUS_STYLES = {
  OPEN: 'bg-blue-100 text-blue-700',
  COUNTED: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
};

export default function CycleCounting({ setSelectedItem }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [countSort, setCountSort] = useState({ field: 'count_id', dir: 'desc' });
  const handleCountSort = useCallback((field) => {
    setCountSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [lineSort, setLineSort] = useState({ field: '', dir: 'asc' });
  const handleLineSort = useCallback((field) => {
    setLineSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [showCreate, setShowCreate] = useState(false);
  const [activeCount, setActiveCount] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      const r = await api.get(`/api/inventory/cycle-count${filter ? `?status=${filter}` : ''}`);
      setCounts(r.data.data || []);
    } catch { toast.error('Failed to load cycle counts'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const openCount = async (countId) => {
    setActiveLoading(true);
    try {
      const r = await api.get(`/api/inventory/cycle-count/${countId}`);
      setActiveCount(r.data);
    } catch { toast.error('Failed to load cycle count'); }
    setActiveLoading(false);
  };

  // ── Active count detail view ──
  if (activeCount) {
    return <CountDetail count={activeCount} setActiveCount={setActiveCount}
      reload={() => { loadCounts(); setActiveCount(null); }}
      setSelectedItem={setSelectedItem} />;
  }

  const ccDefaultCols = [
    { key: 'count_id', label: 'Count ID', width: 120, sortField: 'count_id', visible: true },
    { key: 'location_code', label: 'Location', width: 130, sortField: 'location_code', visible: true },
    { key: 'count_type', label: 'Type', width: 90, sortField: 'count_type', visible: true },
    { key: 'total_items', label: 'Items', width: 70, sortField: 'total_items', align: 'center', visible: true },
    { key: 'counted_items', label: 'Counted', width: 90, sortField: 'counted_items', align: 'center', visible: true },
    { key: 'variance_count', label: 'Variances', width: 90, sortField: 'variance_count', align: 'center', visible: true },
    { key: 'created_by', label: 'Created By', width: 130, sortField: 'created_by', visible: true },
    { key: 'created_at', label: 'Created', width: 120, sortField: 'created_at', visible: true },
    { key: 'status', label: 'Status', width: 100, sortField: 'status', visible: true },
  ];

  const sortedCounts = sortData(counts, countSort.field, countSort.dir);

  const renderCcCell = (col, c) => {
    const lines = c.lines || [];
    const counted = lines.filter(l => l.status === 'COUNTED' || l.status === 'APPLIED').length;
    const variances = lines.filter(l => l.variance && l.variance !== 0).length;
    switch (col.key) {
      case 'count_id': return <span className="font-mono text-primary font-semibold underline decoration-dotted underline-offset-2">{c.cc_number || c.count_id?.slice(0, 8)}</span>;
      case 'location_code': return <span className="font-semibold">{c.location_code}</span>;
      case 'count_type': return <span className="capitalize">{c.count_type || 'cycle'}</span>;
      case 'total_items': return <span>{lines.length}</span>;
      case 'counted_items': return <span>{counted}/{lines.length}{counted === lines.length && lines.length > 0 && <Check className="h-3 w-3 inline ml-1 text-emerald-500" />}</span>;
      case 'variance_count': return variances > 0 ? <span className="text-red-600 font-semibold">{variances}</span> : <span>0</span>;
      case 'created_by': return <span className="text-muted-foreground">{c.created_by}</span>;
      case 'created_at': return <span className="text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}</span>;
      case 'status': return <Badge className={`text-[9px] ${STATUS_STYLES[c.status] || 'bg-slate-100'}`}>{c.status}</Badge>;
      default: return <span>{c[col.key] != null ? String(c[col.key]) : '—'}</span>;
    }
  };

  return (
    <div className="space-y-4" data-testid="cycle-counting-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold flex items-center gap-1.5">
            <ClipboardCheck className="h-4 w-4 text-primary" /> Cycle Counting
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Schedule physical stock counts, record quantities, review variances, and apply adjustments
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-cycle-count-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Cycle Count
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5">
        {['', 'OPEN', 'COUNTED', 'COMPLETED'].map(f => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)} className="text-xs h-7 px-3"
            data-testid={`filter-${f || 'all'}`}>
            {f || 'All'} {f === '' ? `(${counts.length})` : `(${counts.filter(c => c.status === f).length})`}
          </Button>
        ))}
      </div>

      {/* Count list */}
      {loading ? <LoadingSpinner /> : counts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No cycle counts found</p>
          <p className="text-xs mt-1">Create a new cycle count to begin physical stock verification</p>
        </div>
      ) : (
        <InteractiveTable
          tableId="cycle-counting"
          defaultCols={ccDefaultCols}
          data={sortedCounts}
          renderCell={renderCcCell}
          onRowClick={(row) => openCount(row.count_id)}
          sortState={countSort}
          onSort={handleCountSort}
          rowKey="count_id"
        />
      )}

      {activeLoading && <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50"><LoadingSpinner /></div>}

      {/* Create dialog */}
      <CreateCountDialog open={showCreate} onClose={() => setShowCreate(false)}
        onCreated={(id) => { setShowCreate(false); loadCounts(); openCount(id); }} />
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// Create Cycle Count Dialog
// ══════════════════════════════════════════════════════════════════════════

function CreateCountDialog({ open, onClose, onCreated }) {
  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState('');
  const [countType, setCountType] = useState('cycle');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/api/inventory/locations').then(r => setLocations(r.data.data || [])).catch(() => {});
    }
  }, [open]);

  const handleCreate = async () => {
    if (!selectedLoc) { toast.error('Select a location'); return; }
    setCreating(true);
    try {
      const r = await api.post('/api/inventory/cycle-count/create', {
        location_code: selectedLoc, count_type: countType, notes,
      });
      toast.success(r.data.message);
      onCreated(r.data.count_id);
      setSelectedLoc(''); setNotes(''); setCountType('cycle');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create count')); }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New Cycle Count</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-xs font-semibold">Location *</Label>
            <select value={selectedLoc} onChange={e => setSelectedLoc(e.target.value)}
              className="w-full mt-1 h-9 px-3 border rounded-md text-sm bg-background"
              data-testid="cc-location-select">
              <option value="">Select location...</option>
              {locations.filter(l => l.active !== false).map(l => (
                <option key={l.location_code} value={l.location_code}>{l.location_code} — {l.description}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Count Type</Label>
            <div className="flex gap-2 mt-1">
              {['cycle', 'full', 'spot'].map(t => (
                <Button key={t} size="sm" variant={countType === t ? 'default' : 'outline'}
                  onClick={() => setCountType(t)} className="text-xs capitalize h-8"
                  data-testid={`cc-type-${t}`}>{t}</Button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {countType === 'cycle' && 'Regular scheduled count of items at this location'}
              {countType === 'full' && 'Complete physical count of all items at this location'}
              {countType === 'spot' && 'Quick spot-check of specific items'}
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes..." className="mt-1 text-sm" rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} disabled={creating || !selectedLoc}
              data-testid="cc-create-submit">
              {creating ? 'Creating...' : 'Create Count'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// Cycle Count Detail — Record, Review, Apply
// ══════════════════════════════════════════════════════════════════════════

function CountDetail({ count, setActiveCount, reload, setSelectedItem }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [lines, setLines] = useState(count.lines || []);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [countedValues, setCountedValues] = useState({});
  const [lineSort, setLineSort] = useState({ field: '', dir: 'asc' });
  const handleLineSort = useCallback((field) => {
    setLineSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);

  // Initialize counted values from existing data
  useEffect(() => {
    const init = {};
    (count.lines || []).forEach(l => {
      if (l.counted_quantity !== null && l.counted_quantity !== undefined) {
        init[l.item_code] = String(l.counted_quantity);
      }
    });
    setCountedValues(init);
  }, [count.lines]);

  const setCounted = (itemCode, val) => {
    setCountedValues(prev => ({ ...prev, [itemCode]: val }));
  };

  const saveRecordedCounts = async () => {
    const counts = {};
    Object.entries(countedValues).forEach(([ic, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num)) counts[ic] = num;
    });
    if (Object.keys(counts).length === 0) { toast.error('Enter at least one counted quantity'); return; }
    setSaving(true);
    try {
      const r = await api.put(`/api/inventory/cycle-count/${count.count_id}/record`, { counts });
      toast.success(r.data.message);
      // Reload the count
      const updated = await api.get(`/api/inventory/cycle-count/${count.count_id}`);
      setLines(updated.data.lines || []);
      setActiveCount(updated.data);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to save counts')); }
    setSaving(false);
  };

  const applyAdjustments = async () => {
    if (!window.confirm('Apply all variances? This will adjust stock levels to match counted quantities. This action cannot be undone.')) return;
    setApplying(true);
    try {
      const r = await api.post(`/api/inventory/cycle-count/${count.count_id}/apply`);
      toast.success(r.data.message);
      reload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to apply')); }
    setApplying(false);
  };

  const totalItems = lines.length;
  const countedItems = lines.filter(l => l.status === 'COUNTED' || l.status === 'APPLIED').length;
  const varianceItems = lines.filter(l => l.variance && l.variance !== 0).length;
  const isOpen = count.status === 'OPEN';
  const isCounted = count.status === 'COUNTED';
  const isCompleted = count.status === 'COMPLETED';

  return (
    <div className="space-y-4" data-testid="cycle-count-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => setActiveCount(null)}
            className="flex items-center gap-1 text-xs text-primary hover:underline mb-2"
            data-testid="cc-back-btn">
            <ChevronLeft className="w-3.5 h-3.5" /> Back to Cycle Counts
          </button>
          <h3 className="text-base font-bold flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            Cycle Count {count.cc_number || count.count_id?.slice(0, 8)} — {count.location_code}
            <Badge className={`text-[10px] ${STATUS_STYLES[count.status] || ''}`}>{count.status}</Badge>
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {count.count_type?.toUpperCase()} count • {totalItems} items •
            Created by {count.created_by} on {count.created_at ? new Date(count.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}
            {count.notes && ` • ${count.notes}`}
          </p>
        </div>
        <div className="flex gap-2">
          {isOpen && (
            <Button size="sm" onClick={saveRecordedCounts} disabled={saving} data-testid="cc-save-counts">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Counts'}
            </Button>
          )}
          {isCounted && (
            <Button size="sm" variant="destructive" onClick={applyAdjustments} disabled={applying}
              data-testid="cc-apply-btn">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {applying ? 'Applying...' : 'Apply Adjustments'}
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Total Items" value={totalItems} />
        <SummaryCard label="Counted" value={`${countedItems}/${totalItems}`}
          accent={countedItems === totalItems && totalItems > 0 ? 'emerald' : 'blue'} />
        <SummaryCard label="Variances" value={varianceItems}
          accent={varianceItems > 0 ? 'red' : 'emerald'} />
        <SummaryCard label="Status" value={count.status}
          accent={isCompleted ? 'emerald' : isCounted ? 'amber' : 'blue'} />
      </div>

      {/* Items table */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="bg-muted/50 border-b">
            <SortableTableHeader label="#" field="seq" sortState={lineSort} onSort={handleLineSort} className="w-8" />
            <SortableTableHeader label="Item Code" field="item_code" sortState={lineSort} onSort={handleLineSort} />
            <SortableTableHeader label="Description" field="description" sortState={lineSort} onSort={handleLineSort} />
            <SortableTableHeader label="Bin" field="bin_number" sortState={lineSort} onSort={handleLineSort} />
            <SortableTableHeader label="System Qty" field="system_qty" sortState={lineSort} onSort={handleLineSort} className="text-right" />
            <th className="text-center px-3 py-2 font-semibold w-28">Counted Qty</th>
            <SortableTableHeader label="Variance" field="variance" sortState={lineSort} onSort={handleLineSort} className="text-right" />
            <SortableTableHeader label="Status" field="status" sortState={lineSort} onSort={handleLineSort} />
          </tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const enteredVal = countedValues[l.item_code];
              const enteredNum = enteredVal !== undefined ? parseFloat(enteredVal) : null;
              const variance = l.variance !== null && l.variance !== undefined ? l.variance
                : (enteredNum !== null && !isNaN(enteredNum) ? enteredNum - l.system_quantity : null);
              const hasVariance = variance !== null && variance !== 0;

              return (
                <tr key={l.item_code}
                  className={`border-b transition-colors ${hasVariance ? 'bg-red-50/50' : 'hover:bg-accent/20'} ${isOpen ? '' : 'cursor-pointer'}`}
                  onClick={() => { if (!isOpen) setSelectedItem?.(l.item_code); }}
                  data-testid={`cc-line-${l.item_code}`}>
                  <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-primary underline decoration-dotted underline-offset-2 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setSelectedItem?.(l.item_code); }}>
                    {l.item_code}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{l.description?.slice(0, 35)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{l.bin_number || '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold">{l.system_quantity}</td>
                  <td className="px-3 py-2 text-center">
                    {isOpen ? (
                      <Input type="number" min="0" step="1"
                        value={countedValues[l.item_code] ?? ''}
                        onChange={e => setCounted(l.item_code, e.target.value)}
                        className="h-7 w-24 text-xs text-center mx-auto"
                        placeholder="Count..."
                        data-testid={`cc-input-${l.item_code}`} />
                    ) : (
                      <span className="font-semibold">{l.counted_quantity ?? '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {variance !== null ? (
                      <span className={`font-bold flex items-center justify-end gap-0.5 ${variance > 0 ? 'text-emerald-600' : variance < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {variance > 0 ? <PlusCircle className="h-3 w-3" /> : variance < 0 ? <Minus className="h-3 w-3" /> : null}
                        {Math.abs(variance)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <LineStatusBadge status={l.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Variance summary for COUNTED status */}
      {isCounted && varianceItems > 0 && (
        <div className="border border-amber-200 rounded-xl bg-amber-50/50 p-4">
          <h4 className="text-sm font-bold flex items-center gap-1.5 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Variance Summary
          </h4>
          <p className="text-xs text-muted-foreground mb-3">
            {varianceItems} item(s) have discrepancies between system and counted quantities.
            Applying will adjust stock levels to match physical counts.
          </p>
          <div className="space-y-1">
            {lines.filter(l => l.variance && l.variance !== 0).map(l => (
              <div key={l.item_code} className="flex items-center gap-3 text-xs py-1 border-b border-amber-100 last:border-0">
                <span className="font-mono font-semibold text-primary w-32">{l.item_code}</span>
                <span className="text-muted-foreground flex-1">{l.description?.slice(0, 30)}</span>
                <span>System: <strong>{l.system_quantity}</strong></span>
                <span>Counted: <strong>{l.counted_quantity}</strong></span>
                <span className={`font-bold ${l.variance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {l.variance > 0 ? '+' : ''}{l.variance}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed summary */}
      {isCompleted && (
        <div className="border border-emerald-200 rounded-xl bg-emerald-50/50 p-4">
          <h4 className="text-sm font-bold flex items-center gap-1.5 text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> Cycle Count Completed
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            All variances have been applied to stock levels.
            {count.completed_at && ` Completed on ${new Date(count.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz })}`}
          </p>
        </div>
      )}
    </div>
  );
}


function SummaryCard({ label, value, accent = 'slate' }) {
  const colors = {
    slate: 'text-slate-700', blue: 'text-blue-600',
    emerald: 'text-emerald-600', red: 'text-red-600', amber: 'text-amber-600',
  };
  return (
    <div className="border rounded-lg p-3 bg-background">
      <p className={`text-lg font-bold ${colors[accent] || colors.slate}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function LineStatusBadge({ status }) {
  const styles = {
    PENDING: 'bg-slate-100 text-slate-600',
    COUNTED: 'bg-amber-100 text-amber-700',
    APPLIED: 'bg-emerald-100 text-emerald-700',
  };
  return <Badge className={`text-[9px] ${styles[status] || styles.PENDING}`}>{status}</Badge>;
}
